import { useCallback, useEffect, useState, type FormEvent } from "react";
import { FileImage, RefreshCw, Save, ShieldCheck, TestTube2 } from "lucide-react";
import { SectionCard } from "../components/SectionCard";
import { InlineFeedback } from "../components/Feedback";
import { StatusBadge } from "../components/StatusBadge";
import { ApiClientError, api } from "../lib/api";

type AiOcrStatus = {
  credential: { configured: boolean; status: string | null; activeVersion: number | null; fingerprint: string | null; expiresAt: string | null };
  budget: { maxFilesPerDay: number; maxPagesPerDay: number; dailyCostBudgetMinor: number; currency: "CNY"; revision: number } | null;
  latestTest: { status: string; errorCode: string | null; checkedAt: string | null; estimatedCostMinor: number | null; budgetStatus: string; credentialVersion: number | null; policyRevision: number } | null;
  testMatchesCurrent: boolean;
  enabled: boolean;
  readiness: "budget_required" | "credential_unavailable" | "test_outcome_unknown" | "test_required" | "enable_required" | "ready";
};

function actionKey(prefix: string) {
  return `${prefix}-${globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`}`;
}

export function amountToMinor(value: string) {
  const trimmed = value.trim();
  if (trimmed.length > 20) return null;
  const match = /^(0|[1-9]\d*)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (!match) return null;
  const major = match[1];
  if (!major) return null;
  const minor = BigInt(major) * 100n + BigInt((match[2] ?? "").padEnd(2, "0") || "0");
  return minor > 0n && minor <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(minor) : null;
}

function safeFailure(error: unknown, fallback: string) {
  if (error instanceof ApiClientError && [400, 403, 409, 422].includes(error.status)) return error.message;
  return fallback;
}

function statusText(value: string | null | undefined) {
  if (value === "active") return "有效";
  if (value === "success") return "通过";
  if (value === "failed") return "失败";
  if (value === "pending") return "处理中";
  if (value === "within_limit") return "预算内";
  if (value === "over_limit") return "超出预算";
  if (value === "blocked") return "已阻断";
  return "尚未确认";
}

function readinessText(value: AiOcrStatus["readiness"] | undefined) {
  if (value === "budget_required") return "待设置预算";
  if (value === "credential_unavailable") return "凭据不可用";
  if (value === "test_outcome_unknown") return "测试结果待确认";
  if (value === "test_required") return "待完成测试";
  if (value === "enable_required") return "已就绪，等待显式启用";
  if (value === "ready") return "已启用并就绪";
  return "状态待确认";
}

export function AiOcrConfigPanel({ canManage, canTestService, onReload }: { canManage: boolean; canTestService: boolean; onReload: () => Promise<void> }) {
  const [status, setStatus] = useState<AiOcrStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState("");
  const [files, setFiles] = useState("");
  const [pages, setPages] = useState("");
  const [cost, setCost] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<"save" | "test" | "enable" | null>(null);
  const [actionError, setActionError] = useState("");
  const [actionNotice, setActionNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadingError("");
    try {
      const result = await api<AiOcrStatus>("/api/service-config/ai-ocr/status");
      setStatus(result);
      setFiles(result.budget ? String(result.budget.maxFilesPerDay) : "");
      setPages(result.budget ? String(result.budget.maxPagesPerDay) : "");
      setCost(result.budget ? String(result.budget.dailyCostBudgetMinor / 100) : "");
    } catch {
      setLoadingError("票据 OCR 专用配置暂不可读取；页面不会把未知状态显示为可用。");
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function submitBudget(event: FormEvent) {
    event.preventDefault();
    const maxFilesPerDay = Number(files);
    const maxPagesPerDay = Number(pages);
    const dailyCostBudgetMinor = amountToMinor(cost);
    if (!Number.isSafeInteger(maxFilesPerDay) || maxFilesPerDay <= 0 || !Number.isSafeInteger(maxPagesPerDay) || maxPagesPerDay <= 0 || dailyCostBudgetMinor === null) {
      setActionError("请填写大于 0 的每日文件数、页数和人民币预算；金额最多两位小数。");
      return;
    }
    if (reason.trim().length < 8) {
      setActionError("请填写至少 8 个字的调整原因，便于审计。");
      return;
    }
    setBusy("save"); setActionError(""); setActionNotice("");
    try {
      await api("/api/service-config/ai-ocr/budget", { method: "PUT", body: JSON.stringify({ maxFilesPerDay, maxPagesPerDay, dailyCostBudgetMinor, currency: "CNY", reason: reason.trim(), idempotencyKey: actionKey("ai-ocr-budget") }) });
      await load();
      setActionNotice("GLM-OCR 每日预算和容量上限已保存。");
      setReason("");
      await onReload();
    } catch (error) {
      setActionError(safeFailure(error, "票据 OCR 预算保存失败；当前设置未确认保存。"));
    } finally { setBusy(null); }
  }

  async function runSyntheticTest() {
    setBusy("test"); setActionError(""); setActionNotice("");
    try {
      await api("/api/service-config/ai-ocr/connectivity-test", { method: "POST", body: JSON.stringify({ idempotencyKey: actionKey("ai-ocr-synthetic-test") }) });
      await load();
      await onReload();
      setActionNotice("合成票据测试请求已返回，以下方服务端测试证据状态为准。");
    } catch (error) {
      setActionError(safeFailure(error, "GLM-OCR 合成票据连通测试失败。"));
      await load();
    } finally { setBusy(null); }
  }

  async function enable() {
    if (reason.trim().length < 8) { setActionError("请填写至少 8 个字的启用原因，便于审计。"); return; }
    setBusy("enable"); setActionError(""); setActionNotice("");
    try {
      await api("/api/service-config/ai-ocr/enable", { method: "POST", body: JSON.stringify({ reason: reason.trim(), idempotencyKey: actionKey("ai-ocr-enable") }) });
      await load();
      await onReload();
      setActionNotice("票据 OCR 已由服务端确认启用。");
      setReason("");
    } catch (error) {
      setActionError(safeFailure(error, "票据 OCR 启用失败；服务端未确认启用。"));
      await load();
    } finally { setBusy(null); }
  }

  const latest = status?.latestTest;
  const testPassed = status?.testMatchesCurrent === true && latest?.status === "success" && latest.budgetStatus === "within_limit";
  const credentialReady = status?.credential.configured === true && status.credential.status === "active";
  const budgetReady = Boolean(status?.budget && status.budget.maxFilesPerDay > 0 && status.budget.maxPagesPerDay > 0 && status.budget.dailyCostBudgetMinor > 0);
  const ready = credentialReady && budgetReady && testPassed && status?.readiness === "enable_required";

  return <SectionCard icon={FileImage} title="票据 OCR · 智谱 GLM-OCR" description="专用版面解析服务；独立于聊天模型目录、默认路线和聊天连通测试。" meta={<StatusBadge value={status?.enabled ? "verified" : ready ? "configured" : "unverified"} />} variant="ledger">
    {loading && <InlineFeedback tone="info">正在读取服务端密钥元数据、预算策略和测试证据…</InlineFeedback>}
    {loadingError && <InlineFeedback tone="danger">{loadingError} <button type="button" className="button secondary compact" onClick={() => { void load(); }}>重新读取</button></InlineFeedback>}
    {status && <div className="ai-ocr-panel">
      <div className="ai-ocr-readiness" aria-label="票据 OCR 就绪状态">
        <span><small>智谱凭据</small><strong>{credentialReady ? `已配置 · 版本 ${status.credential.activeVersion}` : status.credential.configured ? `不可用 · ${status.credential.status ?? "状态未知"}` : "未配置"}</strong></span>
        <span><small>每日预算</small><strong>{budgetReady ? `¥${(status.budget!.dailyCostBudgetMinor / 100).toFixed(2)} · ${status.budget!.maxFilesPerDay} 个文件 / ${status.budget!.maxPagesPerDay} 页` : "未配置完整"}</strong></span>
        <span><small>合成票据测试</small><strong>{!latest ? "尚无测试证据" : latest.status === "success" && !status.testMatchesCurrent ? "旧测试证据 · 需重测" : `${statusText(latest.status)} · ${statusText(latest.budgetStatus)}`}</strong></span>
        <span><small>运行状态</small><strong>{readinessText(status.readiness)}</strong></span>
      </div>
      {latest && <p className={`ai-ocr-test-result ${testPassed ? "success" : latest.status === "failed" || latest.budgetStatus === "blocked" || latest.budgetStatus === "over_limit" ? "failure" : "pending"}`}>
        {latest.status === "success" && latest.budgetStatus === "within_limit" && status.testMatchesCurrent ? "当前密钥与预算版本的合成测试通过，成本在预算内。" : latest.status === "success" && !status.testMatchesCurrent ? "旧凭据或旧预算测试，需重测；当前配置尚未验证。" : latest.status === "failed" ? `最近一次测试失败${latest.errorCode ? `（${latest.errorCode}）` : ""}。` : latest.status === "pending" ? "测试仍在处理中，尚不能启用。" : "测试状态或费用预算尚未确认，尚不能启用。"}
        {latest.estimatedCostMinor !== null && ` 测试估算费用 ¥${(latest.estimatedCostMinor / 100).toFixed(2)}。`}
        {latest.checkedAt && ` 测试时间：${new Date(latest.checkedAt).toLocaleString("zh-CN")}。`}
      </p>}
      <form className="ai-ocr-budget-form" onSubmit={(event) => { void submitBudget(event); }}>
        <div className="ai-ocr-budget-fields">
          <label>每日文件数上限<input aria-label="每日文件数上限" type="number" min="1" step="1" inputMode="numeric" value={files} onChange={(event) => setFiles(event.target.value)} disabled={!canManage || busy !== null} /></label>
          <label>每日页数上限<input aria-label="每日页数上限" type="number" min="1" step="1" inputMode="numeric" value={pages} onChange={(event) => setPages(event.target.value)} disabled={!canManage || busy !== null} /></label>
          <label>每日 OCR 预算（超额时停止后续调用）<input aria-label="每日 OCR 预算" type="number" min="0.01" step="0.01" inputMode="decimal" value={cost} onChange={(event) => setCost(event.target.value)} disabled={!canManage || busy !== null} /><small>金额按人民币填写；超出预算后服务端会阻断后续调用。</small></label>
        </div>
        <label className="ai-ocr-reason">审计原因<input aria-label="票据 OCR 操作原因" value={reason} onChange={(event) => setReason(event.target.value)} minLength={8} maxLength={500} placeholder="说明此次预算调整或启用原因（至少 8 个字）" disabled={!canManage || busy !== null} /></label>
        <div className="ai-ocr-actions">
          <button type="submit" className="button primary" disabled={!canManage || busy !== null || loading}><Save size={16} />{busy === "save" ? "保存中…" : "保存每日上限"}</button>
          <button type="button" className="button secondary" disabled={!canTestService || busy !== null || loading || !credentialReady || !budgetReady} onClick={() => { void runSyntheticTest(); }}><TestTube2 size={16} />{busy === "test" ? "测试中…" : "测试合成票据"}</button>
          <button type="button" className="button secondary" disabled={!canManage || busy !== null || loading || status.enabled || !ready} onClick={() => { void enable(); }}><ShieldCheck size={16} />{busy === "enable" ? "启用中…" : status.enabled ? "已启用" : "显式启用票据 OCR"}</button>
          <button type="button" className="button ghost" disabled={busy !== null || loading} onClick={() => { void load(); }}><RefreshCw size={16} />刷新状态</button>
        </div>
      </form>
      {actionError && <InlineFeedback tone="danger">{actionError}</InlineFeedback>}
      {actionNotice && <InlineFeedback tone="success">{actionNotice}</InlineFeedback>}
    </div>}
  </SectionCard>;
}
